/**
 * Einheitliche Dashboard-Kachel mit konsistenter Größe.
 * Verwendet für alle KPI-Cards, Summary-Cards in der gesamten App.
 * 
 * Features:
 * - Gleiche min-height für alle Kacheln in einer Reihe
 * - Flex-Layout: Header oben, Wert mittig, Icon rechts
 * - Text-Overflow-Handling mit line-clamp
 * - Vollständig responsiv für Mobile, Tablet, Desktop
 */
import React from 'react';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';

/**
 * @param {Object} props
 * @param {string} props.title - Kachel-Titel (z.B. "Aktive Standorte")
 * @param {string|number} props.value - Hauptwert (z.B. "42" oder "1.234,56 €")
 * @param {React.ReactNode} props.icon - Lucide Icon-Komponente
 * @param {string} [props.colorScheme] - Farbschema: 'blue', 'green', 'purple', 'red', 'amber', 'orange', 'slate'
 * @param {string} [props.href] - Optionaler Link
 * @param {string} [props.className] - Zusätzliche CSS-Klassen
 * @param {string} [props.testId] - data-testid
 */
const DashboardCard = ({
  title,
  value,
  icon: Icon,
  colorScheme = 'slate',
  href,
  className = '',
  testId,
  children
}) => {
  // Farbschemata für konsistentes Styling
  const colorSchemes = {
    blue: {
      card: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200',
      title: 'text-blue-700',
      value: 'text-blue-900',
      iconBg: 'bg-blue-500',
    },
    green: {
      card: 'bg-gradient-to-br from-green-50 to-green-100 border-green-200',
      title: 'text-green-700',
      value: 'text-green-900',
      iconBg: 'bg-green-500',
    },
    purple: {
      card: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200',
      title: 'text-purple-700',
      value: 'text-purple-900',
      iconBg: 'bg-purple-500',
    },
    red: {
      card: 'bg-gradient-to-br from-red-50 to-red-100 border-red-200',
      title: 'text-red-700',
      value: 'text-red-900',
      iconBg: 'bg-red-500',
    },
    amber: {
      card: 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200',
      title: 'text-amber-700',
      value: 'text-amber-900',
      iconBg: 'bg-amber-500',
    },
    orange: {
      card: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200',
      title: 'text-orange-700',
      value: 'text-orange-900',
      iconBg: 'bg-orange-500',
    },
    slate: {
      card: 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200',
      title: 'text-slate-700',
      value: 'text-slate-900',
      iconBg: 'bg-slate-500',
    },
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.slate;

  const cardContent = (
    <Card 
      className={`p-3 sm:p-4 lg:p-6 h-full min-h-[90px] sm:min-h-[100px] lg:min-h-[120px] flex flex-col justify-between card-hover cursor-pointer ${colors.card} ${className}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between h-full gap-2">
        <div className="flex flex-col justify-between h-full flex-1 min-w-0">
          {/* Title mit line-clamp für lange Labels */}
          <p className={`text-xs sm:text-sm font-medium ${colors.title} mb-0.5 sm:mb-1 line-clamp-2`}>
            {title}
          </p>
          {/* Hauptwert - responsive Schriftgröße */}
          <p className={`text-lg sm:text-2xl lg:text-3xl font-bold ${colors.value} truncate`}>
            {value}
          </p>
          {/* Optionaler zusätzlicher Inhalt */}
          {children && <div className="mt-1 sm:mt-2">{children}</div>}
        </div>
        {/* Icon - responsive Größe */}
        {Icon && (
          <div className={`${colors.iconBg} p-1.5 sm:p-2 lg:p-3 rounded-lg sm:rounded-xl flex-shrink-0`}>
            <Icon className="text-white w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
          </div>
        )}
      </div>
    </Card>
  );

  // Wrap in Link wenn href vorhanden
  if (href) {
    return <Link to={href} className="block h-full">{cardContent}</Link>;
  }

  return cardContent;
};

export default DashboardCard;
